import type { NormalizedRect } from '@/domain/types'
import type { GamePreset, HudLocation } from '@/presets/types'
import { drawSourceToImageData, isImageDataSource } from './image'

function isHudBarPixel(r: number, g: number, b: number): boolean {
  const red = r > 105 && r > g * 1.45 && r > b * 1.35
  const blue = b > 90 && b > r * 1.35 && b > g * 1.05
  const exp = g > 90 && r > 80 && b < Math.max(r, g) * 0.75
  return red || blue || exp
}

export class HudLocator {
  locate(
    source: CanvasImageSource | ImageData,
    preset: GamePreset,
    sourceWidth?: number,
    sourceHeight?: number,
  ): HudLocation {
    const { width, height } = sourceDimensions(source, sourceWidth, sourceHeight)
    const fallback = preset.hud.fallback
    const searchStart = Math.floor(height * Math.max(1 - preset.hud.searchBottomRatio, fallback.y))
    // The HP→EXP color bands occupy the middle of the MXDC HUD. Limiting the
    // horizontal scan prevents inventory and hotkey icons from becoming anchors.
    const xStart = Math.floor(width * Math.max(0, fallback.x + fallback.width * 0.25))
    const xEnd = Math.floor(width * Math.min(1, fallback.x + fallback.width * 0.73))
    const searchRect: NormalizedRect = {
      x: xStart / width,
      y: searchStart / height,
      width: Math.max(1, xEnd - xStart) / width,
      height: Math.max(1, height - searchStart) / height,
    }
    const frame = isImageDataSource(source)
      ? source
      : drawSourceToImageData(source, width, height, searchRect)
    const scanXStart = isImageDataSource(source) ? xStart : 0
    const scanXEnd = isImageDataSource(source) ? xEnd : frame.width
    const scanYStart = isImageDataSource(source) ? searchStart : 0
    const yOffset = isImageDataSource(source) ? 0 : searchStart
    const rowScores: { y: number; score: number }[] = []

    for (let y = scanYStart; y < frame.height; y += 2) {
      let matches = 0
      let sampled = 0
      for (let x = scanXStart; x < scanXEnd; x += 2) {
        const offset = (y * frame.width + x) * 4
        if (isHudBarPixel(frame.data[offset] ?? 0, frame.data[offset + 1] ?? 0, frame.data[offset + 2] ?? 0)) matches += 1
        sampled += 1
      }
      rowScores.push({ y, score: sampled ? matches / sampled : 0 })
    }

    const peak = rowScores.reduce((best, row) => (row.score > best.score ? row : best), { y: 0, score: 0 })
    const confidence = Math.min(1, peak.score / 0.11)
    if (confidence < preset.hud.minimumConfidence) {
      return { rect: fallback, confidence, method: 'fallback' }
    }

    const expectedBarCenter = fallback.y + fallback.height * 0.68
    const detectedCenter = (peak.y + yOffset) / height
    const y = Math.min(1 - fallback.height, Math.max(0, fallback.y + detectedCenter - expectedBarCenter))
    const rect: NormalizedRect = { ...fallback, y }
    return { rect, confidence, method: 'detected' }
  }
}

function sourceDimensions(
  source: CanvasImageSource | ImageData,
  fallbackWidth?: number,
  fallbackHeight?: number,
): { width: number; height: number } {
  if (isImageDataSource(source)) return { width: source.width, height: source.height }
  if (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap) return { width: source.width, height: source.height }
  const video = source as HTMLVideoElement
  return {
    width: video.videoWidth || fallbackWidth || 1,
    height: video.videoHeight || fallbackHeight || 1,
  }
}
